import React, { ReactNode, useEffect, useContext } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Breakpoint } from '@mui/system';
import { BasicButton } from '@payer-admin-ui/components-v2/src/buttons/BasicButton';
import { StandardButton, StandardButtonType } from '@payer-admin-ui/components-v2/src/buttons/StandardButton';
import { ModalContext } from '../contexts/ModalProvider';
import { ErrorNotification } from '../Error/ErrorNotification';
import { PayerError } from '../utils/errors';

export type ConfirmDialogProps = {
  title: string;
  children: ReactNode;
  confirmButton: {
    color?: 'error';
    content: ReactNode;
    disabled?: boolean;
  };
  onConfirm: () => void;
  onClosed?: () => void;
  isFetching?: boolean;
  success?: boolean;
  error?: ReactNode | PayerError;
  fullWidth?: boolean;
  maxWidth?: Breakpoint | false;
};

export const ConfirmDialog = ({
  title,
  children,
  confirmButton: { color: confirmButtonColor, content: confirmButtonContent, disabled: confirmButtonDisabled = false },
  onConfirm,
  onClosed,
  isFetching,
  success,
  error,
  fullWidth = false,
  maxWidth,
}: ConfirmDialogProps) => {
  const { isModalOpen, closeModal } = useContext(ModalContext);

  useEffect(() => {
    if (success) {
      // TODO add snackbar message reflecting success
      closeModal();
    }
  }, [success, closeModal]);

  return (
    <Dialog
      open={isModalOpen}
      onClose={closeModal}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onTransitionExited={onClosed}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {children}
        {error && <ErrorNotification>{error}</ErrorNotification>}
      </DialogContent>
      <DialogActions>
        <StandardButton type={StandardButtonType.CANCEL} onClick={closeModal} />
        <BasicButton
          onClick={onConfirm}
          color={confirmButtonColor}
          loading={isFetching}
          disabled={confirmButtonDisabled}
        >
          {confirmButtonContent}
        </BasicButton>
      </DialogActions>
    </Dialog>
  );
};
